import { useMutation } from '@apollo/client';
import { Visibility } from '@mui/icons-material';
import { Grid, Tooltip, IconButton } from '@mui/material';

import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { truncate } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import MARK_NOTIFICATION_AS_READ from 'gql/mutations/markNotificationAsRead.gql';

import { ReactComponent as CalendarIcon } from 'assets/metro-calendar.svg';
import { translations } from 'locales/i18n';
import { SelectionNotification as ISelectionNotification } from 'types/AppTypes';

interface Props {
  notification: ISelectionNotification;
}

export default function SelectionNotification({ notification }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [markNotificationAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
    refetchQueries: ['GetNotificationsForUser']
  });

  if (!notification.moduleSelection) return null;

  return (
    <Grid
      item
      xs={12}
      className={clsx('notifications', {
        'notifications--new': !notification.viewedAt
      })}
    >
      <Grid container>
        <Grid item className="notifications__logo">
          <CalendarIcon style={{ width: '50px', height: '60px' }} />
        </Grid>
        <Grid
          item
          xs
          onClick={() => {
            markNotificationAsRead({
              variables: { userId: notification.userId, notificationId: notification.id }
            }).finally(() => {
              navigate(
                `/${notification.moduleSelection?.subjectType.toLocaleLowerCase()}/student/module-selections/${
                  notification.moduleSelection?.id
                }`
              );
            });
          }}
        >
          <Grid container className="notifications__content" justifyContent="space-between">
            <Grid item xs className="notifications__content__headline">
              {truncate(notification.moduleSelection.title, { length: 60, separator: ' ' })}
            </Grid>
            <Grid item xs="auto" className="notifications__content__date">
              <Tooltip
                title={new Date(notification.createdAt).toLocaleString()}
                placement="left"
                PopperProps={{ style: { zIndex: 2000 } }}
              >
                <span>
                  {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid item xs className="notifications__description">
            {t(translations.pages.selectionNotifications.info())}
          </Grid>
        </Grid>
        <Grid
          container
          className="notifications__actions"
          justifyContent="flex-end"
          onClick={() => {
            markNotificationAsRead({
              variables: { userId: notification.userId, notificationId: notification.id }
            });
          }}
        >
          <Tooltip
            title={<>{t(translations.layout.profile.markAsRead())}</>}
            placement="left"
            PopperProps={{ style: { zIndex: 2000 } }}
          >
            <IconButton className="notifications__actions__mark-as-read">
              <Visibility />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );
}
